/**
 * OWL2 Factory Functions - Consolidated Export
 *
 * Re-exports all factory functions from the types module.
 * Factory functions are also available from their respective type modules.
 */

// IRI factories
export { fullIRI, abbreviatedIRI, parseIRI } from '../types/IRI.ts';

// Literal factories
export { typedLiteral, plainLiteral } from '../types/Literal.ts';

// Entity factories
export {
    classEntity,
    datatypeEntity,
    objectPropertyEntity,
    dataPropertyEntity,
    annotationPropertyEntity,
    namedIndividualEntity,
    namedIndividual,
    anonymousIndividual
} from '../types/Entity.ts';

// Property expression factories
export {
    objectProperty,
    objectInverseOf,
    dataProperty,
    objectPropertyChain
} from '../types/PropertyExpression.ts';

// Data range factories
export {
    datatype,
    dataIntersectionOf,
    dataUnionOf,
    dataComplementOf,
    dataOneOf,
    facetRestriction,
    datatypeRestriction
} from '../types/DataRange.ts';

// Class expression factories
export {
    owlClass,
    objectIntersectionOf,
    objectUnionOf,
    objectComplementOf,
    objectOneOf,
    objectSomeValuesFrom,
    objectAllValuesFrom,
    objectHasValue,
    objectHasSelf,
    objectMinCardinality,
    objectMaxCardinality,
    objectExactCardinality,
    dataSomeValuesFrom,
    dataAllValuesFrom,
    dataHasValue,
    dataMinCardinality,
    dataMaxCardinality,
    dataExactCardinality
} from '../types/ClassExpression.ts';

// Annotation factories
export {
    annotationProperty,
    annotation,
    annotationWithLiteral,
    annotationWithIRI
} from '../types/Annotation.ts';

// Ontology factories
export {
    prefixDeclaration,
    importDeclaration,
    emptyOntology,
    ontology,
    addPrefix,
    addImport,
    addOntologyAnnotation,
    addAxiom,
    addAxioms
} from '../types/Ontology.ts';

// =============================================================================
// Axiom Factory Functions
// =============================================================================

import type { Annotation } from '../types/Annotation.ts';
import type {
    Declaration,
    SubClassOf,
    EquivalentClasses,
    DisjointClasses,
    DisjointUnion,
    SubObjectPropertyOf,
    EquivalentObjectProperties,
    DisjointObjectProperties,
    InverseObjectProperties,
    ObjectPropertyDomain,
    ObjectPropertyRange,
    FunctionalObjectProperty,
    InverseFunctionalObjectProperty,
    ReflexiveObjectProperty,
    IrreflexiveObjectProperty,
    SymmetricObjectProperty,
    AsymmetricObjectProperty,
    TransitiveObjectProperty,
    SubDataPropertyOf,
    EquivalentDataProperties,
    DisjointDataProperties,
    DataPropertyDomain,
    DataPropertyRange,
    FunctionalDataProperty,
    DatatypeDefinition,
    HasKey,
    SameIndividual,
    DifferentIndividuals,
    ClassAssertion,
    ObjectPropertyAssertion,
    NegativeObjectPropertyAssertion,
    DataPropertyAssertion,
    NegativeDataPropertyAssertion,
    AnnotationAssertion,
    SubAnnotationPropertyOf,
    AnnotationPropertyDomain,
    AnnotationPropertyRange
} from '../types/Axiom.ts';
import type { Entity, Individual } from '../types/Entity.ts';
import type { ClassExpression, Class } from '../types/ClassExpression.ts';
import type { DataRange, Datatype } from '../types/DataRange.ts';
import type {
    ObjectPropertyExpression,
    DataPropertyExpression,
    SubObjectPropertyExpression
} from '../types/PropertyExpression.ts';
import type { AnnotationProperty, AnnotationSubject, AnnotationValue } from '../types/Annotation.ts';
import type { IRI } from '../types/IRI.ts';
import type { Literal } from '../types/Literal.ts';

/**
 * Create a Declaration axiom.
 */
export function declaration(entity: Entity, annotations?: readonly Annotation[]): Declaration {
    return {
        axiomType: 'Declaration',
        entity,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a SubClassOf axiom.
 */
export function subClassOf(
    subClass: ClassExpression,
    superClass: ClassExpression,
    annotations?: readonly Annotation[]
): SubClassOf {
    return {
        axiomType: 'SubClassOf',
        subClass,
        superClass,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an EquivalentClasses axiom.
 */
export function equivalentClasses(
    first: ClassExpression,
    second: ClassExpression,
    ...rest: ClassExpression[]
): EquivalentClasses {
    return {
        axiomType: 'EquivalentClasses',
        classExpressions: [first, second, ...rest]
    };
}

/**
 * Create a DisjointClasses axiom.
 */
export function disjointClasses(
    first: ClassExpression,
    second: ClassExpression,
    ...rest: ClassExpression[]
): DisjointClasses {
    return {
        axiomType: 'DisjointClasses',
        classExpressions: [first, second, ...rest]
    };
}

/**
 * Create a DisjointUnion axiom.
 */
export function disjointUnion(
    disjointClass: Class,
    first: ClassExpression,
    second: ClassExpression,
    ...rest: ClassExpression[]
): DisjointUnion {
    return {
        axiomType: 'DisjointUnion',
        disjointClass,
        classExpressions: [first, second, ...rest]
    };
}

/**
 * Create a SubObjectPropertyOf axiom.
 */
export function subObjectPropertyOf(
    subProperty: SubObjectPropertyExpression,
    superProperty: ObjectPropertyExpression,
    annotations?: readonly Annotation[]
): SubObjectPropertyOf {
    return {
        axiomType: 'SubObjectPropertyOf',
        subProperty,
        superProperty,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an EquivalentObjectProperties axiom.
 */
export function equivalentObjectProperties(
    first: ObjectPropertyExpression,
    second: ObjectPropertyExpression,
    ...rest: ObjectPropertyExpression[]
): EquivalentObjectProperties {
    return {
        axiomType: 'EquivalentObjectProperties',
        properties: [first, second, ...rest]
    };
}

/**
 * Create a DisjointObjectProperties axiom.
 */
export function disjointObjectProperties(
    first: ObjectPropertyExpression,
    second: ObjectPropertyExpression,
    ...rest: ObjectPropertyExpression[]
): DisjointObjectProperties {
    return {
        axiomType: 'DisjointObjectProperties',
        properties: [first, second, ...rest]
    };
}

/**
 * Create an InverseObjectProperties axiom.
 */
export function inverseObjectProperties(
    property1: ObjectPropertyExpression,
    property2: ObjectPropertyExpression,
    annotations?: readonly Annotation[]
): InverseObjectProperties {
    return {
        axiomType: 'InverseObjectProperties',
        property1,
        property2,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an ObjectPropertyDomain axiom.
 */
export function objectPropertyDomainAxiom(
    property: ObjectPropertyExpression,
    domain: ClassExpression,
    annotations?: readonly Annotation[]
): ObjectPropertyDomain {
    return {
        axiomType: 'ObjectPropertyDomain',
        property,
        domain,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an ObjectPropertyRange axiom.
 */
export function objectPropertyRangeAxiom(
    property: ObjectPropertyExpression,
    range: ClassExpression,
    annotations?: readonly Annotation[]
): ObjectPropertyRange {
    return {
        axiomType: 'ObjectPropertyRange',
        property,
        range,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a FunctionalObjectProperty axiom.
 */
export function functionalObjectProperty(
    property: ObjectPropertyExpression,
    annotations?: readonly Annotation[]
): FunctionalObjectProperty {
    return {
        axiomType: 'FunctionalObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an InverseFunctionalObjectProperty axiom.
 */
export function inverseFunctionalObjectProperty(
    property: ObjectPropertyExpression,
    annotations?: readonly Annotation[]
): InverseFunctionalObjectProperty {
    return {
        axiomType: 'InverseFunctionalObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a ReflexiveObjectProperty axiom.
 */
export function reflexiveObjectProperty(
    property: ObjectPropertyExpression,
    annotations?: readonly Annotation[]
): ReflexiveObjectProperty {
    return {
        axiomType: 'ReflexiveObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an IrreflexiveObjectProperty axiom.
 */
export function irreflexiveObjectProperty(
    property: ObjectPropertyExpression,
    annotations?: readonly Annotation[]
): IrreflexiveObjectProperty {
    return {
        axiomType: 'IrreflexiveObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a SymmetricObjectProperty axiom.
 */
export function symmetricObjectProperty(
    property: ObjectPropertyExpression,
    annotations?: readonly Annotation[]
): SymmetricObjectProperty {
    return {
        axiomType: 'SymmetricObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an AsymmetricObjectProperty axiom.
 */
export function asymmetricObjectProperty(
    property: ObjectPropertyExpression,
    annotations?: readonly Annotation[]
): AsymmetricObjectProperty {
    return {
        axiomType: 'AsymmetricObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a TransitiveObjectProperty axiom.
 */
export function transitiveObjectProperty(
    property: ObjectPropertyExpression,
    annotations?: readonly Annotation[]
): TransitiveObjectProperty {
    return {
        axiomType: 'TransitiveObjectProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a SubDataPropertyOf axiom.
 */
export function subDataPropertyOf(
    subProperty: DataPropertyExpression,
    superProperty: DataPropertyExpression,
    annotations?: readonly Annotation[]
): SubDataPropertyOf {
    return {
        axiomType: 'SubDataPropertyOf',
        subProperty,
        superProperty,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an EquivalentDataProperties axiom.
 */
export function equivalentDataProperties(
    first: DataPropertyExpression,
    second: DataPropertyExpression,
    ...rest: DataPropertyExpression[]
): EquivalentDataProperties {
    return {
        axiomType: 'EquivalentDataProperties',
        properties: [first, second, ...rest]
    };
}

/**
 * Create a DisjointDataProperties axiom.
 */
export function disjointDataProperties(
    first: DataPropertyExpression,
    second: DataPropertyExpression,
    ...rest: DataPropertyExpression[]
): DisjointDataProperties {
    return {
        axiomType: 'DisjointDataProperties',
        properties: [first, second, ...rest]
    };
}

/**
 * Create a DataPropertyDomain axiom.
 */
export function dataPropertyDomainAxiom(
    property: DataPropertyExpression,
    domain: ClassExpression,
    annotations?: readonly Annotation[]
): DataPropertyDomain {
    return {
        axiomType: 'DataPropertyDomain',
        property,
        domain,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a DataPropertyRange axiom.
 */
export function dataPropertyRangeAxiom(
    property: DataPropertyExpression,
    range: DataRange,
    annotations?: readonly Annotation[]
): DataPropertyRange {
    return {
        axiomType: 'DataPropertyRange',
        property,
        range,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a FunctionalDataProperty axiom.
 */
export function functionalDataProperty(
    property: DataPropertyExpression,
    annotations?: readonly Annotation[]
): FunctionalDataProperty {
    return {
        axiomType: 'FunctionalDataProperty',
        property,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a DatatypeDefinition axiom.
 */
export function datatypeDefinition(
    datatypeRef: Datatype,
    dataRange: DataRange,
    annotations?: readonly Annotation[]
): DatatypeDefinition {
    return {
        axiomType: 'DatatypeDefinition',
        datatype: datatypeRef,
        dataRange,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a HasKey axiom.
 */
export function hasKey(
    classExpression: ClassExpression,
    objectPropertyExpressions: readonly ObjectPropertyExpression[],
    dataPropertyExpressions: readonly DataPropertyExpression[],
    annotations?: readonly Annotation[]
): HasKey {
    return {
        axiomType: 'HasKey',
        classExpression,
        objectPropertyExpressions,
        dataPropertyExpressions,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a SameIndividual axiom.
 */
export function sameIndividual(
    first: Individual,
    second: Individual,
    ...rest: Individual[]
): SameIndividual {
    return {
        axiomType: 'SameIndividual',
        individuals: [first, second, ...rest]
    };
}

/**
 * Create a DifferentIndividuals axiom.
 */
export function differentIndividuals(
    first: Individual,
    second: Individual,
    ...rest: Individual[]
): DifferentIndividuals {
    return {
        axiomType: 'DifferentIndividuals',
        individuals: [first, second, ...rest]
    };
}

/**
 * Create a ClassAssertion axiom.
 */
export function classAssertion(
    classExpression: ClassExpression,
    individual: Individual,
    annotations?: readonly Annotation[]
): ClassAssertion {
    return {
        axiomType: 'ClassAssertion',
        classExpression,
        individual,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an ObjectPropertyAssertion axiom.
 */
export function objectPropertyAssertion(
    property: ObjectPropertyExpression,
    sourceIndividual: Individual,
    targetIndividual: Individual,
    annotations?: readonly Annotation[]
): ObjectPropertyAssertion {
    return {
        axiomType: 'ObjectPropertyAssertion',
        property,
        sourceIndividual,
        targetIndividual,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a NegativeObjectPropertyAssertion axiom.
 */
export function negativeObjectPropertyAssertion(
    property: ObjectPropertyExpression,
    sourceIndividual: Individual,
    targetIndividual: Individual,
    annotations?: readonly Annotation[]
): NegativeObjectPropertyAssertion {
    return {
        axiomType: 'NegativeObjectPropertyAssertion',
        property,
        sourceIndividual,
        targetIndividual,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a DataPropertyAssertion axiom.
 */
export function dataPropertyAssertion(
    property: DataPropertyExpression,
    sourceIndividual: Individual,
    targetValue: Literal,
    annotations?: readonly Annotation[]
): DataPropertyAssertion {
    return {
        axiomType: 'DataPropertyAssertion',
        property,
        sourceIndividual,
        targetValue,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a NegativeDataPropertyAssertion axiom.
 */
export function negativeDataPropertyAssertion(
    property: DataPropertyExpression,
    sourceIndividual: Individual,
    targetValue: Literal,
    annotations?: readonly Annotation[]
): NegativeDataPropertyAssertion {
    return {
        axiomType: 'NegativeDataPropertyAssertion',
        property,
        sourceIndividual,
        targetValue,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an AnnotationAssertion axiom.
 */
export function annotationAssertion(
    property: AnnotationProperty,
    subject: AnnotationSubject,
    value: AnnotationValue,
    annotations?: readonly Annotation[]
): AnnotationAssertion {
    return {
        axiomType: 'AnnotationAssertion',
        property,
        subject,
        value,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create a SubAnnotationPropertyOf axiom.
 */
export function subAnnotationPropertyOf(
    subProperty: AnnotationProperty,
    superProperty: AnnotationProperty,
    annotations?: readonly Annotation[]
): SubAnnotationPropertyOf {
    return {
        axiomType: 'SubAnnotationPropertyOf',
        subProperty,
        superProperty,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an AnnotationPropertyDomain axiom.
 */
export function annotationPropertyDomainAxiom(
    property: AnnotationProperty,
    domain: IRI,
    annotations?: readonly Annotation[]
): AnnotationPropertyDomain {
    return {
        axiomType: 'AnnotationPropertyDomain',
        property,
        domain,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}

/**
 * Create an AnnotationPropertyRange axiom.
 */
export function annotationPropertyRangeAxiom(
    property: AnnotationProperty,
    range: IRI,
    annotations?: readonly Annotation[]
): AnnotationPropertyRange {
    return {
        axiomType: 'AnnotationPropertyRange',
        property,
        range,
        ...(annotations && annotations.length > 0 && { annotations })
    };
}
